// Stock movement per event — shows qtyIn / qtySold / qtyReturnedToGudang
// and the resulting loss for every product allocated to the event.
//
// Works for both active and closed events: allocations persist after the
// event closes, and `qtyReturnedToGudang` is finalised at close time.
//
// Lives separately from the sales summary in `getEventSummary` because:
//   - Stock allocation is owned by the event itself (subcollection), not
//     derived from transactions, so the data path is different.
//   - For closed events, this is the only place to look at stock — sales
//     summaries don't include the warehouse return.

import { Package2, AlertTriangle, CheckCheck, PackageCheck } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import { formatDate, formatNumber } from '@/lib/format';

export interface StockMovementTableProps {
  eventId: string;
  /** Whether the event is closed. Affects labels + tone only — data shape is the same. */
  isClosed: boolean;
}

export function StockMovementTable({ eventId, isClosed }: StockMovementTableProps) {
  const { data: allocations, isLoading } = useEventAllocations(eventId);

  if (isLoading) {
    return (
      <Card>
        <div className="py-8 text-center text-ink-subtle text-small">Loading stock…</div>
      </Card>
    );
  }

  if (!allocations || allocations.length === 0) {
    return (
      <Card>
        <div className="flex items-start gap-3 py-3">
          <div className="w-10 h-10 rounded bg-line/30 text-ink-subtle flex items-center justify-center shrink-0">
            <Package2 size={18} />
          </div>
          <div>
            <h3 className="text-subhead font-display font-semibold text-ink">
              Stock movement
            </h3>
            <p className="text-small text-ink-subtle mt-1">
              Event ini tidak punya alokasi stok, jadi tidak ada perpindahan stok untuk ditampilkan.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Aggregate per row
  const rows = allocations.map((a) => {
    const sold = a.qtySold;
    const returned = a.qtyReturnedToGudang;
    const loss = Math.max(0, a.qtyIn - sold - returned);
    const remainingOnHand = Math.max(0, a.qtyIn - sold - returned);
    return {
      ...a,
      sold,
      returned,
      loss,
      remainingOnHand,
    };
  });

  const totals = rows.reduce(
    (acc, r) => ({
      qtyIn: acc.qtyIn + r.qtyIn,
      sold: acc.sold + r.sold,
      returned: acc.returned + r.returned,
      loss: acc.loss + r.loss,
    }),
    { qtyIn: 0, sold: 0, returned: 0, loss: 0 },
  );

  const hasLoss = totals.loss > 0;
  const fullyReconciled = isClosed && !hasLoss;

  return (
    <Card padding="none">
      <div className="p-6 pb-4 border-b border-line flex items-start justify-between gap-3">
        <div>
          <h3 className="text-subhead font-display font-semibold text-ink">
            Stock movement
          </h3>
          <p className="text-caption text-ink-subtle mt-1">
            {isClosed
              ? 'Stock alokasi, penjualan, dan pengembalian ke gudang saat event ditutup.'
              : 'Stock alokasi dan penjualan. Pengembalian ke gudang dicatat saat event ditutup.'}
          </p>
        </div>
        {fullyReconciled ? (
          <span className="inline-flex items-center gap-1.5 text-caption font-medium text-success">
            <CheckCheck size={14} />
            Semua kembali
          </span>
        ) : hasLoss ? (
          <span className="inline-flex items-center gap-1.5 text-caption font-medium text-danger">
            <AlertTriangle size={14} />
            Loss {formatNumber(totals.loss)} unit
          </span>
        ) : null}
      </div>

      <Table>
        <THead>
          <TR>
            <TH>Produk</TH>
            <TH>Lot / ED</TH>
            <TH className="text-right">Masuk (qty in)</TH>
            <TH className="text-right">Keluar (sold)</TH>
            <TH className="text-right">Kembali ke gudang</TH>
            <TH className="text-right">Loss</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((r) => (
            // Keyed by allocation id: one product can be carried in several
            // lots, and a productId key made React reuse rows across them.
            <TR key={r.id}>
              <TD>
                <p className="font-medium text-ink">{r.productName}</p>
                <code className="text-caption font-mono text-ink-subtle">
                  {r.productSku}
                </code>
              </TD>
              <TD className="text-caption">
                {r.lotNumber || r.expiryDate ? (
                  <>
                    {r.lotNumber && <span className="font-mono text-ink">{r.lotNumber}</span>}
                    {r.lotNumber && r.expiryDate && ' · '}
                    {r.expiryDate && <span className="text-ink-subtle">ED {formatDate(r.expiryDate)}</span>}
                  </>
                ) : (
                  <span className="text-ink-subtle">—</span>
                )}
              </TD>
              <TD className="text-right tabular-nums">{formatNumber(r.qtyIn)}</TD>
              <TD className="text-right tabular-nums">{formatNumber(r.sold)}</TD>
              <TD className="text-right tabular-nums">
                {r.returned > 0 ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <PackageCheck size={12} />
                    {formatNumber(r.returned)}
                  </span>
                ) : (
                  <span className="text-ink-subtle">—</span>
                )}
              </TD>
              <TD className="text-right tabular-nums">
                {r.loss > 0 ? (
                  <span className="text-danger font-medium">
                    −{formatNumber(r.loss)}
                  </span>
                ) : (
                  <span className="text-ink-subtle">—</span>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
        <tfoot>
          <TR className="bg-canvas/50 font-medium">
            <TD className="text-overline uppercase text-ink-subtle font-medium" colSpan={2}>Total</TD>
            <TD className="text-right tabular-nums">{formatNumber(totals.qtyIn)}</TD>
            <TD className="text-right tabular-nums">{formatNumber(totals.sold)}</TD>
            <TD className="text-right tabular-nums">{formatNumber(totals.returned)}</TD>
            <TD className="text-right tabular-nums">
              {hasLoss ? (
                <span className="text-danger">−{formatNumber(totals.loss)}</span>
              ) : (
                <span className="text-ink-subtle">—</span>
              )}
            </TD>
          </TR>
        </tfoot>
      </Table>
    </Card>
  );
}
